import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ArrowRight, Check, Clock, Flag, Inbox, LayoutList, Loader2, MapPin, Star, Users, X } from "lucide-react";
import { supabase } from "@/lib/supabase";
import StatCard from "@/components/ui/stat-card";
import { StatModal, ModalSection, BreakdownGrid } from "@/components/ui/stat-modal";
import { AreaChart, BarList } from "@/components/ui/mini-charts";

export interface OverviewKPIs {
  total_users: number; new_users_7d: number; new_users_30d: number; blocked_users: number;
  total_listings: number; active_listings: number; pending_listings: number; paused_listings: number;
  rejected_listings: number; new_listings_7d: number; new_listings_30d: number;
  total_conversations: number; new_conversations_7d: number; total_messages: number; new_messages_7d: number;
  total_reviews: number; avg_rating: number | string; reported_reviews: number;
}
export interface GrowthPoint { label: string; users: number; listings: number }
export interface PendingItem {
  id: string; title: string; category: string; price: number; price_unit: string;
  city: string; state: string; images: string[]; owner_name: string; created_at: string;
}
interface Details {
  users_by_plan: { plan: string; count: number }[];
  rating_distribution: { stars: number; count: number }[];
  listings_by_category: { category: string; count: number }[];
  recent_users: { id: string; name: string | null; avatar_url: string | null; city: string | null; created_at: string; blocked: boolean }[];
  reported_reviews: { id: string; rating: number; comment: string | null; report_reason: string | null; created_at: string; listing_title: string | null }[];
  recent_reviews: { id: string; rating: number; comment: string | null; created_at: string; listing_title: string | null; reviewer_name: string | null }[];
}
type ModalKey = "users" | "listings" | "pending" | "rating" | null;

interface Props {
  kpis: OverviewKPIs;
  growth: GrowthPoint[];
  periodControl?: ReactNode;
  pending: PendingItem[];
  pendingLoading?: boolean;
  onApprove: (id: string) => Promise<void> | void;
  onReject: (id: string) => void;
  onGoTo: (tab: "listings" | "users" | "stores", status?: string) => void;
}

const brl = (v: number) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const dt = (s: string) => new Date(s).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
const stars = (n: number) => "★".repeat(n) + "☆".repeat(Math.max(0, 5 - n));

function GoButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button onClick={onClick}
      className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#1a4a2e] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#2d7a4d]">
      {children} <ArrowRight className="h-4 w-4" />
    </button>
  );
}

function Row({ k, v, cls = "" }: { k: string; v: ReactNode; cls?: string }) {
  return (
    <div className="flex justify-between border-b border-dashed border-[#f0ede4] py-2.5 text-[13.5px] last:border-0">
      <span className="text-[#8a9088]">{k}</span>
      <span className={`font-bold ${cls}`}>{v}</span>
    </div>
  );
}

const Empty = ({ children }: { children: ReactNode }) => (
  <div className="py-6 text-center text-sm text-[#8a9088]">{children}</div>
);

export default function AdminOverview({ kpis: rawKpis, growth, periodControl, pending, pendingLoading, onApprove, onReject, onGoTo }: Props) {
  const kpis = useMemo(
    () => Object.fromEntries(Object.entries(rawKpis).map(([k, v]) => [k, Number(v) || 0])) as unknown as OverviewKPIs,
    [rawKpis],
  );
  const [open, setOpen] = useState<ModalKey>(null);
  const [details, setDetails] = useState<Details | null>(null);
  const [detailsErr, setDetailsErr] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    supabase.rpc("admin_overview_details").then(({ data, error }) => {
      if (error) { console.error("admin_overview_details:", error); setDetailsErr(true); return; }
      setDetails(data as Details);
    });
  }, [kpis]);

  const avg = Number(kpis.avg_rating) || 0;
  const labels = useMemo(() => growth.map((g) => g.label), [growth]);
  const usersSeries = useMemo(() => growth.map((g) => g.users), [growth]);
  const listingsSeries = useMemo(() => growth.map((g) => g.listings), [growth]);
  const close = (o: boolean) => { if (!o) setOpen(null); };

  const detailsFallback = !details && !detailsErr ? <Empty>Carregando…</Empty> : <Empty>Não foi possível carregar.</Empty>;

  async function approve(id: string) {
    setBusyId(id);
    try { await onApprove(id); } finally { setBusyId(null); }
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
        <StatCard label="Usuários" value={kpis.total_users} delta={`↑ ${kpis.new_users_7d} esta semana`}
          spark={usersSeries.slice(-14)} onClick={() => setOpen("users")} />
        <StatCard label="Anúncios ativos" value={kpis.active_listings} delta={`↑ ${kpis.new_listings_7d} esta semana`}
          spark={listingsSeries.slice(-14)} onClick={() => setOpen("listings")} />
        <StatCard label="Aguardando aprovação" value={kpis.pending_listings} highlight
          badge={kpis.pending_listings > 0 ? "exige ação" : "tudo em dia"} onClick={() => setOpen("pending")} />
        <StatCard label="Nota média" value={avg} decimals={1} suffix="★"
          delta={`${kpis.total_reviews} avaliações`} deltaTone="neutral" onClick={() => setOpen("rating")} />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.7fr_1fr]">
        <section className="rounded-2xl border border-[#ebe7dc] bg-white p-4 sm:p-5">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 className="text-[13px] font-bold text-[#1a4a2e]">Crescimento da plataforma</h3>
              <p className="text-xs text-[#8a9088]">Novos cadastros e anúncios</p>
            </div>
            {periodControl}
          </div>
          <AreaChart labels={labels} height={190} series={[
            { name: "Usuários", color: "#1a4a2e", values: usersSeries },
            { name: "Anúncios", color: "#e0a021", values: listingsSeries },
          ]} />
        </section>

        <section className="rounded-2xl border border-[#ebe7dc] bg-white p-4 sm:p-5">
          <h3 className="text-[13px] font-bold text-[#1a4a2e]">Resumo operacional</h3>
          <p className="mb-2 text-xs text-[#8a9088]">Status atual</p>
          <Row k="Anúncios pendentes" v={kpis.pending_listings} cls="text-[#c98a1a]" />
          <Row k="Pausados" v={kpis.paused_listings} cls="text-[#d97a2b]" />
          <Row k="Rejeitados" v={kpis.rejected_listings} cls="text-[#c0493b]" />
          <Row k="Conversas (7d)" v={kpis.new_conversations_7d} cls="text-[#2d7a4d]" />
          <Row k="Mensagens (7d)" v={kpis.new_messages_7d} cls="text-[#2d7a4d]" />
          <Row k="Avaliações reportadas" v={kpis.reported_reviews} cls={kpis.reported_reviews > 0 ? "text-[#c0493b]" : "text-[#2d7a4d]"} />
          <Row k="Usuários bloqueados" v={kpis.blocked_users} cls={kpis.blocked_users > 0 ? "text-[#c0493b]" : "text-[#2d7a4d]"} />
        </section>
      </div>

      {/* POP-UP: USUARIOS */}
      <StatModal open={open === "users"} onOpenChange={close} icon={<Users />} title="Usuários"
        subtitle={`${kpis.total_users} cadastrados na plataforma`}
        footer={<GoButton onClick={() => { setOpen(null); onGoTo("users"); }}>Ver todos os usuários</GoButton>}>
        <BreakdownGrid cols={4} items={[
          { label: "Total", value: kpis.total_users },
          { label: "Novos (7d)", value: kpis.new_users_7d, tone: "ok" },
          { label: "Novos (30d)", value: kpis.new_users_30d, tone: "info" },
          { label: "Bloqueados", value: kpis.blocked_users, tone: kpis.blocked_users ? "rej" : "default" },
        ]} />
        <ModalSection title="Por plano">
          {details ? <BarList items={details.users_by_plan.map((p) => ({
            label: p.plan, value: p.count,
            tone: /ultra/i.test(p.plan) ? "star" : /pro/i.test(p.plan) ? "ok" : "default",
          }))} /> : detailsFallback}
        </ModalSection>
        <ModalSection title="Novos cadastros no período">
          <AreaChart labels={labels} series={[{ name: "Usuários", color: "#1a4a2e", values: usersSeries }]} />
        </ModalSection>
        <ModalSection title="Últimos cadastros">
          {details ? (details.recent_users.length ? (
            <ul className="divide-y divide-[#f0ede4]">
              {details.recent_users.map((u) => (
                <li key={u.id} className="flex items-center gap-3 py-2">
                  {u.avatar_url
                    ? <img src={u.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover" />
                    : <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#eaf2ec] text-xs font-bold text-[#2d7a4d]">{(u.name || "?").slice(0, 1).toUpperCase()}</span>}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">{u.name || "Sem nome"}</div>
                    <div className="text-xs text-[#8a9088]">{u.city || "—"}</div>
                  </div>
                  {u.blocked && <span className="rounded-full bg-[#f7e7e4] px-2 py-0.5 text-[10.5px] font-bold text-[#c0493b]">Bloqueado</span>}
                  <span className="text-xs text-[#8a9088]">{dt(u.created_at)}</span>
                </li>
              ))}
            </ul>
          ) : <Empty>Nenhum cadastro ainda.</Empty>) : detailsFallback}
        </ModalSection>
      </StatModal>

      {/* POP-UP: ANUNCIOS */}
      <StatModal open={open === "listings"} onOpenChange={close} icon={<LayoutList />} title="Anúncios"
        subtitle={`${kpis.total_listings} anúncios no total`}
        footer={<GoButton onClick={() => { setOpen(null); onGoTo("listings", "active"); }}>Gerenciar anúncios</GoButton>}>
        <BreakdownGrid cols={4} items={[
          { label: "Total", value: kpis.total_listings },
          { label: "Ativos", value: kpis.active_listings, tone: "ok" },
          { label: "Novos (7d)", value: kpis.new_listings_7d, tone: "info" },
          { label: "Novos (30d)", value: kpis.new_listings_30d, tone: "info" },
        ]} />
        <ModalSection title="Por status">
          <BarList items={[
            { label: "Ativos", value: kpis.active_listings, tone: "ok" },
            { label: "Pendentes", value: kpis.pending_listings, tone: "pend" },
            { label: "Pausados", value: kpis.paused_listings, tone: "warn" },
            { label: "Rejeitados", value: kpis.rejected_listings, tone: "rej" },
          ]} />
        </ModalSection>
        <ModalSection title="Ativos por categoria">
          {details ? (details.listings_by_category.length
            ? <BarList items={details.listings_by_category.map((c) => ({ label: c.category, value: c.count, tone: "ok" as const }))} />
            : <Empty>Nenhum anúncio ativo.</Empty>) : detailsFallback}
        </ModalSection>
        <ModalSection title="Novos anúncios no período">
          <AreaChart labels={labels} series={[{ name: "Anúncios", color: "#e0a021", values: listingsSeries }]} />
        </ModalSection>
      </StatModal>

      {/* POP-UP: AGUARDANDO APROVACAO */}
      <StatModal open={open === "pending"} onOpenChange={close} icon={<Clock />} title="Aguardando aprovação"
        subtitle={kpis.pending_listings ? `${kpis.pending_listings} anúncio(s) para revisar` : "Nenhum anúncio na fila"}
        footer={<GoButton onClick={() => { setOpen(null); onGoTo("listings", "pending"); }}>Abrir moderação completa</GoButton>}>
        {pendingLoading ? <Empty><Loader2 className="mx-auto h-5 w-5 animate-spin" /></Empty> : pending.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-[#ebe7dc] bg-white py-10 text-center">
            <Inbox className="h-8 w-8 text-[#2d7a4d]" />
            <div className="font-semibold text-[#1a4a2e]">{kpis.pending_listings > 0 ? "Pendentes fora desta lista" : "Tudo em dia"}</div>
            <div className="text-sm text-[#8a9088]">{kpis.pending_listings > 0 ? "Abra a moderação completa para revisar." : "Não há anúncios aguardando aprovação."}</div>
          </div>
        ) : (
          <ul className="space-y-2.5">
            {pending.map((l) => (
              <li key={l.id} className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#ebe7dc] bg-white p-3 transition-colors hover:border-[#2d7a4d] sm:flex-nowrap">
                {l.images?.[0]
                  ? <img src={l.images[0]} alt="" className="h-14 w-14 shrink-0 rounded-xl object-cover" />
                  : <span className="h-14 w-14 shrink-0 rounded-xl bg-[#eaf2ec]" />}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{l.title}</div>
                  <div className="truncate text-xs text-[#8a9088]">{l.category} · {l.owner_name}</div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-[#8a9088]">
                    <span className="font-semibold text-[#2d7a4d]">{brl(l.price)}/{l.price_unit}</span>
                    <span className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{l.city}/{l.state}</span>
                    <span>· {dt(l.created_at)}</span>
                  </div>
                </div>
                <div className="flex w-full gap-2 sm:w-auto">
                  <button onClick={() => approve(l.id)} disabled={busyId === l.id}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#2d7a4d] px-3 py-2 text-[13px] font-semibold text-white transition hover:bg-[#1a4a2e] disabled:opacity-60 sm:flex-none">
                    {busyId === l.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Aprovar
                  </button>
                  <button onClick={() => { setOpen(null); onReject(l.id); }} disabled={busyId === l.id}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-[#eccbc6] bg-white px-3 py-2 text-[13px] font-semibold text-[#c0493b] transition hover:bg-[#f7e7e4] disabled:opacity-60 sm:flex-none">
                    <X className="h-3.5 w-3.5" /> Rejeitar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </StatModal>

      {/* POP-UP: NOTA MEDIA */}
      <StatModal open={open === "rating"} onOpenChange={close} icon={<Star />} title="Avaliações"
        subtitle={`Nota média ${avg.toFixed(1).replace(".", ",")} de 5`}>
        <BreakdownGrid items={[
          { label: "Nota média", value: avg, decimals: 1, suffix: " ★", tone: "star" },
          { label: "Avaliações", value: kpis.total_reviews },
          { label: "Reportadas", value: kpis.reported_reviews, tone: kpis.reported_reviews ? "rej" : "ok" },
        ]} />
        <ModalSection title="Distribuição das notas">
          {details ? <BarList items={details.rating_distribution.map((r) => ({
            label: `${r.stars} ${stars(r.stars)}`, value: r.count, tone: r.stars >= 4 ? "star" : r.stars === 3 ? "pend" : "rej",
          }))} /> : detailsFallback}
        </ModalSection>
        <ModalSection title="Reportadas">
          {details ? (details.reported_reviews.length ? (
            <ul className="space-y-2">
              {details.reported_reviews.map((r) => (
                <li key={r.id} className="rounded-xl border border-[#eccbc6] bg-[#fdf6f5] p-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-[#c0493b]"><Flag className="mr-1 inline h-3 w-3" />{r.report_reason || "Sem motivo"}</span>
                    <span className="text-[#8a9088]">{dt(r.created_at)}</span>
                  </div>
                  <div className="mt-1 text-sm"><span className="text-[#e0a021]">{stars(r.rating)}</span> {r.comment || <i className="text-[#8a9088]">sem comentário</i>}</div>
                  {r.listing_title && <div className="mt-0.5 text-xs text-[#8a9088]">em {r.listing_title}</div>}
                </li>
              ))}
            </ul>
          ) : <Empty>Nenhuma avaliação reportada.</Empty>) : detailsFallback}
        </ModalSection>
        <ModalSection title="Mais recentes">
          {details ? (details.recent_reviews.length ? (
            <ul className="divide-y divide-[#f0ede4]">
              {details.recent_reviews.map((r) => (
                <li key={r.id} className="py-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[#e0a021]">{stars(r.rating)}</span>
                    <span className="text-[#8a9088]">{dt(r.created_at)}</span>
                  </div>
                  <div className="text-sm">{r.comment || <i className="text-[#8a9088]">sem comentário</i>}</div>
                  <div className="text-xs text-[#8a9088]">{r.reviewer_name || "Anônimo"}{r.listing_title ? ` · ${r.listing_title}` : ""}</div>
                </li>
              ))}
            </ul>
          ) : <Empty>Nenhuma avaliação ainda.</Empty>) : detailsFallback}
        </ModalSection>
      </StatModal>
    </div>
  );
}
